/**
 * Step 3 — Applications (PRD §7.4): compensation with show-on-post toggle,
 * candidate requirements (typed eligibility enums — no protected attributes),
 * the nationality requirement with its mandatory policy note + "Documented and
 * approved" acknowledgement, and the question builder.
 *
 * Candidate location is country-relative: the option labels name the job's own
 * country, city and region, so the same five ids read correctly wherever the
 * role is filed.
 */
import { useEffect, useMemo, useRef } from 'react';
import type { JobEligibilityLocation } from '@tabbio/types';
import { Pressable, StyleSheet, View } from 'react-native';
import { Text, type TextInput } from '@/components/common/AppText';
import { useTranslation } from 'react-i18next';
import { Check } from 'lucide-react-native';
import { colors, spacing } from '@tabbio/tokens';
import { useTheme } from '../../../hooks/useTheme';
import { useHaptics } from '../../../hooks/useHaptics';
import { NativeForm } from '../../common/NativeForm';
import {
  CANDIDATE_LOCATION_VALUES,
  EXPERIENCE_YEARS_VALUES,
  LANGUAGE_VALUES,
  NATIONALITY_VALUES,
  SALARY_CURRENCIES,
  WORK_ELIGIBILITY_VALUES,
} from './post-job-machine';
import {
  getOptionLabel,
  getRegionLabelKeyForCountry,
  CITY_OPTIONS,
  COUNTRY_OPTIONS,
} from '../post-job-flow.data';
import { QuestionBuilder } from './QuestionBuilder';
import {
  FieldLabel,
  FlowCard,
  FlowChips,
  FlowDropdown,
  FlowTextField,
  InlineError,
  fieldStyles,
  type FlowOption,
} from './post-job-ui';
import { AppSwitch } from '../../common/AppSwitch';
import type { PostJobFlowController } from './use-post-job-flow';

function useEmployerOptions<V extends string>(
  values: readonly V[],
  keyPrefix: string,
): FlowOption<V>[] {
  const { t } = useTranslation('employer');
  return useMemo(
    () =>
      values.map((value) => ({
        value,
        label: t(`${keyPrefix}.${value}` as never) as string,
      })),
    [keyPrefix, t, values],
  );
}

/**
 * Candidate-location options labelled against the job's own location.
 *
 * "In {country}" / "In {city}" / "Across {region}" only mean something once the
 * job says where it is, so each tier is dropped when its half is missing — an
 * employer who has not picked a city never sees an "In —" row. "Anywhere" and
 * "Open to relocating" always apply and always show.
 */
function useCandidateLocationOptions(
  country: string,
  city: string,
  selected: JobEligibilityLocation,
): FlowOption<JobEligibilityLocation>[] {
  const { t } = useTranslation('employer');
  const { t: tCommon } = useTranslation('common');

  return useMemo(() => {
    const countryLabelKey = getOptionLabel(COUNTRY_OPTIONS, country);
    const cityLabelKey = getOptionLabel(CITY_OPTIONS, city);
    const regionLabelKey = getRegionLabelKeyForCountry(country);

    // A country/city the wizard does not know (an imported job's free text) is
    // still a real place name, so it labels the row as typed.
    const countryName = countryLabelKey
      ? (tCommon(countryLabelKey as never) as string)
      : country.trim();
    const cityName = cityLabelKey
      ? (tCommon(cityLabelKey as never) as string)
      : city.trim();
    const regionName = regionLabelKey
      ? (tCommon(regionLabelKey as never) as string)
      : '';

    // A tier whose half is missing is normally dropped — an "In —" row helps
    // nobody. The one exception is the value already stored on the job: an
    // imported role can hold `in_city` without a recorded city, and hiding its
    // own answer would make the field read as empty.
    const generic = (tier: 'in_country' | 'in_city' | 'in_region') =>
      tier === selected
        ? (t(`jobFlow.options.candidateLocation.${tier}_generic`) as string)
        : null;

    const labelFor = (value: JobEligibilityLocation): string | null => {
      switch (value) {
        case 'in_country':
          return countryName
            ? (t('jobFlow.options.candidateLocation.in_country', {
                country: countryName,
              }) as string)
            : generic('in_country');
        case 'in_city':
          return cityName
            ? (t('jobFlow.options.candidateLocation.in_city', {
                city: cityName,
              }) as string)
            : generic('in_city');
        case 'in_region':
          return regionName
            ? (t('jobFlow.options.candidateLocation.in_region', {
                region: regionName,
              }) as string)
            : generic('in_region');
        case 'open_to_relocation':
          return countryName
            ? (t('jobFlow.options.candidateLocation.open_to_relocation', {
                country: countryName,
              }) as string)
            : (t(
                'jobFlow.options.candidateLocation.open_to_relocation_any',
              ) as string);
        default:
          return t('jobFlow.options.candidateLocation.any') as string;
      }
    };

    return CANDIDATE_LOCATION_VALUES.reduce<
      FlowOption<JobEligibilityLocation>[]
    >((acc, value) => {
      const label = labelFor(value);
      if (label) acc.push({ value, label });
      return acc;
    }, []);
  }, [city, country, selected, t, tCommon]);
}

export function PostJobApplicationsStep({
  flow,
}: {
  flow: PostJobFlowController;
}) {
  const { t } = useTranslation('employer');
  const { theme } = useTheme();
  const haptics = useHaptics();
  const minRef = useRef<TextInput>(null);
  const noteRef = useRef<TextInput>(null);

  const {
    state,
    setCompensation,
    setEligibility,
    setWorkforce,
    errorFor,
    focusFieldId,
    clearFocusField,
  } = flow;

  const locationOptions = useCandidateLocationOptions(
    state.basics.country,
    state.basics.city,
    state.eligibility.candidateLocation,
  );
  const experienceOptions = useEmployerOptions(
    EXPERIENCE_YEARS_VALUES,
    'jobFlow.options.experienceYears',
  );
  const languageOptions = useEmployerOptions(
    LANGUAGE_VALUES,
    'jobFlow.options.language',
  );
  const workEligibilityOptions = useEmployerOptions(
    WORK_ELIGIBILITY_VALUES,
    'jobFlow.options.workEligibility',
  );
  const nationalityOptions = useEmployerOptions(
    NATIONALITY_VALUES,
    'jobFlow.options.nationality',
  );
  const currencyOptions = useMemo(
    () => SALARY_CURRENCIES.map((value) => ({ value, label: value })),
    [],
  );
  const periodOptions = useMemo(
    () =>
      (['monthly', 'yearly'] as const).map((value) => ({
        value,
        label: t(`jobFlow.options.period.${value}` as never) as string,
      })),
    [t],
  );

  useEffect(() => {
    if (focusFieldId === 'salaryMin' || focusFieldId === 'salaryMax') {
      minRef.current?.focus();
      clearFocusField();
    } else if (
      focusFieldId === 'workforceNote' ||
      focusFieldId === 'workforceAck'
    ) {
      noteRef.current?.focus();
      clearFocusField();
    }
  }, [clearFocusField, focusFieldId]);

  const programme = state.eligibility.workforceProgramme;

  return (
    <NativeForm style={styles.wrap}>
      {/* Compensation */}
      <View style={fieldStyles.group}>
        <FieldLabel>{t('jobFlow.salary.title') as string}</FieldLabel>
        <FlowCard>
          <View style={fieldStyles.twoColumn}>
            <View style={fieldStyles.flexOne}>
              <FlowTextField
                ref={minRef}
                label={t('jobFlow.salary.min') as string}
                value={state.compensation.min}
                onChangeText={(min) => setCompensation({ min })}
                placeholder={t('jobFlow.salary.minPlaceholder') as string}
                keyboardType="numeric"
                errorKey={errorFor('salaryMin')}
              />
            </View>
            <View style={fieldStyles.flexOne}>
              <FlowTextField
                label={t('jobFlow.salary.max') as string}
                value={state.compensation.max}
                onChangeText={(max) => setCompensation({ max })}
                placeholder={t('jobFlow.salary.maxPlaceholder') as string}
                keyboardType="numeric"
                errorKey={errorFor('salaryMax')}
              />
            </View>
          </View>

          <View style={fieldStyles.twoColumn}>
            <View style={fieldStyles.flexOne}>
              <FlowDropdown
                label={t('jobFlow.salary.currency') as string}
                value={state.compensation.currency}
                placeholder="AED"
                options={currencyOptions}
                onChange={(currency) => setCompensation({ currency })}
              />
            </View>
            <View style={fieldStyles.flexOne}>
              <FlowChips
                label={t('jobFlow.salary.period') as string}
                value={state.compensation.period}
                options={periodOptions}
                onChange={(period) => setCompensation({ period })}
              />
            </View>
          </View>

          <View style={fieldStyles.switchRow}>
            <View style={fieldStyles.switchTextWrap}>
              <Text style={[fieldStyles.switchTitle, { color: theme.text }]}>
                {t('jobFlow.salary.showOnPost')}
              </Text>
              <Text
                style={[
                  fieldStyles.switchSubtitle,
                  { color: theme.textTertiary },
                ]}
              >
                {t('jobFlow.salary.showOnPostHint')}
              </Text>
            </View>
            <AppSwitch
              value={state.compensation.visible}
              onValueChange={(visible) => setCompensation({ visible })}
              accessibilityLabel={t('jobFlow.salary.showOnPost') as string}
            />
          </View>
        </FlowCard>
      </View>

      {/* Candidate requirements */}
      <View style={fieldStyles.group}>
        <FieldLabel>
          {t('jobFlow.requirementsGroup.title') as string}
        </FieldLabel>
        <View style={fieldStyles.twoColumn}>
          <View style={fieldStyles.flexOne}>
            <FlowDropdown
              label={t('jobFlow.fields.candidateLocation') as string}
              value={state.eligibility.candidateLocation}
              placeholder={t('jobFlow.options.candidateLocation.any') as string}
              options={locationOptions}
              onChange={(candidateLocation) =>
                setEligibility({ candidateLocation })
              }
            />
          </View>
          <View style={fieldStyles.flexOne}>
            <FlowDropdown
              label={t('jobFlow.fields.experienceYears') as string}
              value={state.eligibility.experienceYears}
              placeholder={t('jobFlow.options.experienceYears.any') as string}
              options={experienceOptions}
              onChange={(experienceYears) =>
                setEligibility({ experienceYears })
              }
            />
          </View>
        </View>
        <View style={fieldStyles.twoColumn}>
          <View style={fieldStyles.flexOne}>
            <FlowDropdown
              label={t('jobFlow.fields.language') as string}
              value={state.eligibility.language}
              placeholder={t('jobFlow.options.language.any') as string}
              options={languageOptions}
              onChange={(language) => setEligibility({ language })}
            />
          </View>
          <View style={fieldStyles.flexOne}>
            <FlowDropdown
              label={t('jobFlow.fields.workEligibility') as string}
              value={state.eligibility.workEligibility}
              placeholder={t('jobFlow.options.workEligibility.any') as string}
              options={workEligibilityOptions}
              onChange={(workEligibility) =>
                setEligibility({ workEligibility })
              }
            />
          </View>
        </View>
      </View>

      {/* Nationality (fail-closed: note + acknowledgement required) */}
      <View style={fieldStyles.group}>
        <FlowDropdown
          label={t('jobFlow.fields.nationality') as string}
          value={programme.value}
          placeholder={t('jobFlow.options.nationality.none') as string}
          options={nationalityOptions}
          onChange={(value) =>
            setWorkforce(
              value === 'none'
                ? { value, note: '', acknowledged: false }
                : { value },
            )
          }
        />

        {/* Says why the field exists at all, so it is not read as a free
            demographic filter. */}
        <Text style={[styles.fieldHint, { color: theme.textTertiary }]}>
          {t('jobFlow.workforce.hint')}
        </Text>

        {programme.value !== 'none' ? (
          <FlowCard>
            <FlowTextField
              ref={noteRef}
              label={t('jobFlow.workforce.note') as string}
              value={programme.note}
              onChangeText={(note) => setWorkforce({ note })}
              placeholder={t('jobFlow.workforce.notePlaceholder') as string}
              errorKey={errorFor('workforceNote')}
              multiline
              minHeight={88}
            />

            <Pressable
              onPress={() => {
                haptics.trigger('selection');
                setWorkforce({ acknowledged: !programme.acknowledged });
              }}
              accessibilityRole="checkbox"
              accessibilityState={{ checked: programme.acknowledged }}
              style={styles.ackRow}
            >
              <View
                style={[
                  styles.checkbox,
                  {
                    borderColor: programme.acknowledged
                      ? colors.primary
                      : theme.border,
                    backgroundColor: programme.acknowledged
                      ? colors.primary
                      : 'transparent',
                  },
                ]}
              >
                {programme.acknowledged ? (
                  <Check size={13} color="#ffffff" strokeWidth={3} />
                ) : null}
              </View>
              <View style={fieldStyles.switchTextWrap}>
                <Text style={[fieldStyles.switchTitle, { color: theme.text }]}>
                  {t('jobFlow.workforce.acknowledged')}
                </Text>
                <Text
                  style={[
                    fieldStyles.switchSubtitle,
                    { color: theme.textTertiary },
                  ]}
                >
                  {t('jobFlow.workforce.acknowledgedHint')}
                </Text>
              </View>
            </Pressable>
            <InlineError messageKey={errorFor('workforceAck')} />
          </FlowCard>
        ) : null}
      </View>

      <QuestionBuilder flow={flow} />
    </NativeForm>
  );
}

const styles = StyleSheet.create({
  wrap: {
    gap: spacing[6],
  },
  fieldHint: {
    fontSize: 13,
    lineHeight: 18,
  },
  ackRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing[3],
  },
  checkbox: {
    width: 22,
    height: 22,
    borderRadius: 7,
    borderWidth: 2,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 1,
  },
});
